import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import customBookingFormFieldObject from '@/entities/customBookingFormField/interface'

import {
  ALL_FORMS_QUERY,
  SINGLE_FORM_QUERY,
  FORMS_DEPENDENTS_QUERY,
  CREATE_FORM_MUTATION,
  UPDATE_FORM_MUTATION,
  DELETE_FORM_MUTATION,
  FORCE_DELETE_FORM_MUTATION,
  ADD_FORM_FIELDS_MUTATION,
  REMOVE_FORM_FIELDS_MUTATION,
  CREATE_FORM_FIELDS_MUTATION,
  DELETE_FORM_FIELDS_MUTATION,
  UPDATE_FORM_FIELDS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const customBookingFormRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_FORM_QUERY, variables).then((data: any) => {
      return hydrateObject(data.form)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_FORMS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.forms)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_FORM_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.form),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_FORMS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.forms),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(FORMS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([FORMS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_FORM_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.form)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_FORM_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.form)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_FORM_MUTATION, variables).then((data: any) => {
      return data.deleteCustomBookingForm
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_FORM_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCustomBookingForm
    })
  },

  assignFields: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        fieldIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_FORM_FIELDS_MUTATION, variables)
  },

  unassignFields: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        fieldIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_FORM_FIELDS_MUTATION, variables)
  },

  createFields: async (id: string, items: Array<customBookingFormFieldObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        fields: items,
      },
    }
    return graphQLClient.request(CREATE_FORM_FIELDS_MUTATION, variables)
  },

  deleteFields: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        fieldIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_FORM_FIELDS_MUTATION, variables)
  },

  updateFieldsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        fieldIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_FORM_FIELDS_ORDER_MUTATION, variables)
  },
}

export default customBookingFormRepository
